import { exactCurrencyFormatter } from "@/utils"
import { coding_amount, split_uncoded_cents } from "@/utils/transaction_amount"
import { TransactionJson } from "@/api"

/**
 * Warning banners for a transaction's coding state: an under/over-assigned amount and splits
 * missing account codes. Early-pay invoices only book their discount, so splits are validated
 * against the coding amount (the discount), not the full invoice face amount.
 */
export function StatusIndicators({ transaction, className }: { transaction: TransactionJson; className?: string }) {
  const transaction_amount = coding_amount(transaction.kind, transaction.invoice_amount, transaction.discount_amount)
  const transaction_cents = Math.round(transaction_amount * 100)

  const total_split_cents = transaction.splits.reduce((sum: number, split: any) => {
    return sum + Math.round(split.amount * 100)
  }, 0)

  // Compare magnitudes so a positive-amount deduction coded with negative splits still reads as coded.
  const uncoded_cents = split_uncoded_cents(transaction_amount, total_split_cents)
  const hasUncodedAmount = uncoded_cents !== 0

  const splitsWithoutAccountId = transaction.splits.filter((split: any) => !split.account_id)
  const hasSplitsWithoutAccountId = splitsWithoutAccountId.length > 0

  if (!hasUncodedAmount && !hasSplitsWithoutAccountId) {
    return null
  }

  const totalLabel = transaction.kind === "invoice" ? "Discount total" : "Transaction total"

  return (
    <div class={className ?? "mx-4 mt-4 space-y-2"}>
      {hasUncodedAmount && (
        <div class="flex items-center gap-2 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r">
          <div class="flex-shrink-0">
            <svg class="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium text-yellow-800">
              {uncoded_cents > 0
                ? `${exactCurrencyFormatter(uncoded_cents / 100)} more is assigned than the ${totalLabel.toLowerCase()}`
                : `${exactCurrencyFormatter(Math.abs(uncoded_cents / 100))} still needs to be assigned to account codes`}
            </p>
            <p class="text-xs text-yellow-700 mt-1">
              {totalLabel}: {exactCurrencyFormatter(Math.abs(transaction_cents) / 100)} • Currently assigned:{" "}
              {exactCurrencyFormatter(total_split_cents / 100)}
            </p>
          </div>
        </div>
      )}

      {hasSplitsWithoutAccountId && (
        <div class="flex items-center gap-2 p-3 bg-orange-50 border-l-4 border-orange-400 rounded-r">
          <div class="flex-shrink-0">
            <svg class="w-5 h-5 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium text-orange-800">
              {splitsWithoutAccountId.length === 1
                ? "One line item hasn't been assigned to an account code"
                : `${splitsWithoutAccountId.length} line items haven't been assigned to account codes`}
            </p>
            <p class="text-xs text-orange-700 mt-1">Please assign account codes to complete the transaction coding</p>
          </div>
        </div>
      )}
    </div>
  )
}
